import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Brain, Loader2, TrendingUp, TrendingDown, ArrowUpRight, Octagon, RefreshCw, Trophy, Users } from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";

const CARDS = [
  { key: "q1_contributed_fr", title: "Ce qui a contribué à votre réussite",
    icon: TrendingUp, tone: "#0B7A3B", accent: "#DCFCE7", tid: "learning-q1" },
  { key: "q2_wasted_fr",      title: "Ce qui vous a fait perdre du temps",
    icon: TrendingDown, tone: "#B91C1C", accent: "#FEE2E2", tid: "learning-q2" },
  { key: "q3_do_more_fr",     title: "Ce que vous devriez faire davantage",
    icon: ArrowUpRight, tone: "#0B2545", accent: "#EEF4FF", tid: "learning-q3" },
  { key: "q4_stop_fr",        title: "Ce que vous devriez arrêter",
    icon: Octagon, tone: "#7A5B14", accent: "#FDF5DC", tid: "learning-q4" },
];

export default function AbiLearning() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);

  const load = async (recompute = false) => {
    if (recompute) setRecomputing(true); else setLoading(true);
    try {
      const r = await api.get("/abi/copilot/learning/monthly-recap",
                                { params: { recompute } });
      setReport(r.data);
      if (recompute) toast.success("Récap mensuel régénéré.");
    } catch (e) {
      toast.error("Erreur de chargement.");
    } finally {
      setLoading(false); setRecomputing(false);
    }
  };

  useEffect(() => { load(false); }, []);

  if (loading || !report) return (
    <div className="p-8 flex items-center gap-2" data-testid="abi-learning-loading">
      <Loader2 size={16} className="animate-spin" /> Chargement du récap…
    </div>
  );

  const totalTimeH = Math.round((report.total_time_minutes || 0) / 60 * 10) / 10;
  const weights = report.learning_weights || {};

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FBF6EA" }} data-testid="abi-learning-root">
      <TopBar />

      {/* Hero */}
      <div className="px-8 pt-8 pb-6"
            style={{ background: "linear-gradient(120deg,#0B2545 0%,#123B6E 60%,#1D5AA5 100%)" }}>
        <div className="max-w-6xl mx-auto text-ivory">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest opacity-80">
            <Brain size={14} /> ALTIORA · Moteur d'apprentissage
          </div>
          <h1 className="text-3xl md:text-4xl font-serif mt-2 leading-tight text-white"
              data-testid="abi-learning-title">
            {report.headline_fr || "Ce que j'ai appris ce mois-ci"}
          </h1>
          <p className="text-white/85 mt-3 max-w-3xl">{report.intro_fr}</p>
          <div className="mt-4 flex flex-wrap items-center gap-3 text-[13px]">
            <span className="px-3 py-1 rounded-full"
                  style={{ backgroundColor: "rgba(197,168,128,0.20)", color: "#F5D687" }}
                  data-testid="abi-learning-profession">
              Profession : {report.profession_label_fr}
            </span>
            <span className="px-3 py-1 rounded-full"
                  style={{ backgroundColor: "rgba(255,255,255,0.10)", color: "#fff" }}>
              Fenêtre : {report.window_days} jours
            </span>
            <span className="px-3 py-1 rounded-full"
                  style={{ backgroundColor: "rgba(255,255,255,0.10)", color: "#fff" }}>
              Temps enregistré : {totalTimeH} h
            </span>
            <button onClick={() => load(true)} disabled={recomputing}
                    className="ml-auto flex items-center gap-2 px-3 py-1 rounded-full"
                    style={{ backgroundColor: "#C5A880", color: "#0B2545" }}
                    data-testid="abi-learning-recompute">
              {recomputing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Recalculer
            </button>
          </div>
        </div>
      </div>

      {/* 4 cards */}
      <div className="max-w-6xl mx-auto px-8 py-8 grid md:grid-cols-2 gap-5">
        {CARDS.map(c => {
          const items = report[c.key] || [];
          const Icon = c.icon;
          return (
            <div key={c.key} data-testid={c.tid}
                  className="rounded-lg shadow-sm p-5"
                  style={{ backgroundColor: c.accent,
                            borderLeft: `4px solid ${c.tone}` }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded flex items-center justify-center"
                      style={{ backgroundColor: c.tone, color: "#fff" }}>
                  <Icon size={16} />
                </div>
                <h3 className="font-serif text-lg" style={{ color: c.tone }}>
                  {c.title}
                </h3>
              </div>
              <ul className="space-y-2">
                {items.map((s, i) => (
                  <li key={i} className="text-[14px] leading-relaxed text-gray-900">
                    · {s}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Detail — ROI par catégorie */}
      <div className="max-w-6xl mx-auto px-8 pb-4">
        <div className="bg-white rounded-lg shadow-sm p-6" data-testid="abi-learning-roi-panel">
          <h3 className="font-serif text-xl mb-1" style={{ color: "#0B2545" }}>
            Rendement mesuré par activité
          </h3>
          <p className="text-[13px] text-gray-600 mb-4">
            ROI = base × résultats / temps. Les catégories &gt; 1.0 seront amplifiées
            le mois prochain ; celles &lt; 1.0 verront leur score d'impact modéré.
          </p>
          <div className="grid md:grid-cols-2 gap-2 text-[13px]">
            {Object.entries(report.roi_by_cat || {}).sort((a, b) => b[1] - a[1]).map(([cat, roi]) => {
              const w = weights[cat];
              const label = (report.q1_contributed_fr || []).find(x => x.includes(cat)) ? cat : cat;
              const barColor = roi >= 1.2 ? "#0B7A3B" : (roi <= 0.6 ? "#B91C1C" : "#7A5B14");
              return (
                <div key={cat} className="flex items-center gap-3"
                      data-testid={`abi-learning-roi-${cat}`}>
                  <div className="w-28 text-gray-700 capitalize">{label}</div>
                  <div className="flex-1 bg-gray-100 h-2 rounded overflow-hidden">
                    <div className="h-full" style={{
                      width: `${Math.min(100, Math.round(roi * 50))}%`,
                      backgroundColor: barColor,
                    }} />
                  </div>
                  <div className="w-20 text-right font-mono text-[12px]" style={{ color: barColor }}>
                    ROI {roi.toFixed(2)}
                  </div>
                  {w !== undefined && w !== 1 && (
                    <span className="w-14 text-right text-[11px] font-mono"
                          style={{ color: w >= 1 ? "#0B7A3B" : "#B91C1C" }}>
                      ×{w.toFixed(2)}
                    </span>
                  )}
                </div>
              );
            })}
            {Object.keys(report.roi_by_cat || {}).length === 0 && (
              <div className="text-gray-500 italic">
                Aucun signal de temps enregistré ce mois-ci — publiez des signaux
                (temps passé + actions réalisées) pour permettre l'analyse.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Benchmark Intelligence — vs les meilleurs de la profession */}
      {report.benchmark_comparison && (
        <div className="max-w-6xl mx-auto px-8 pb-10">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden"
                data-testid="abi-learning-benchmark-panel">
            <div className="p-6 border-b" style={{ borderColor: "#E5E7EB",
                  background: "linear-gradient(120deg,#0B2545 0%,#123B6E 100%)" }}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 text-white">
                  <div className="w-10 h-10 rounded flex items-center justify-center"
                        style={{ backgroundColor: "#C5A880", color: "#0B2545" }}>
                    <Trophy size={20} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest opacity-80">
                      Benchmark Intelligence · Vous vs les meilleurs
                    </div>
                    <h3 className="font-serif text-xl mt-0.5">
                      Ce que font les 10 % des meilleurs {report.benchmark_comparison.profession_label_fr}
                    </h3>
                  </div>
                </div>
                <div className="text-right text-white/85 text-[11px] leading-tight">
                  <div className="uppercase tracking-widest text-white/60">Source</div>
                  <div className="font-mono">
                    {report.benchmark_comparison.source === "documented"
                      ? "Documenté"
                      : report.benchmark_comparison.source === "cohort"
                          ? "Cohorte observée"
                          : "Hybride"}
                  </div>
                  {report.benchmark_comparison.sample_size > 0 && (
                    <div className="flex items-center justify-end gap-1 mt-1 text-white/60">
                      <Users size={11} /> {report.benchmark_comparison.sample_size} pros
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 space-y-5">
              {/* Mentor narrative */}
              <div className="text-[15px] leading-relaxed text-gray-900 border-l-4 pl-4"
                    style={{ borderColor: "#C5A880" }}
                    data-testid="abi-benchmark-narrative">
                {report.benchmark_comparison.narrative_fr}
              </div>

              {/* Category bars — user vs benchmark */}
              <div>
                <div className="text-[11px] uppercase tracking-widest font-bold mb-2"
                      style={{ color: "#7A5B14" }}>
                  Répartition du temps — vous vs benchmark
                </div>
                <div className="space-y-2">
                  {(report.benchmark_comparison.gaps || []).slice(0, 6).map(g => {
                    const isLever = report.benchmark_comparison.main_lever?.category === g.category;
                    const isOverload = report.benchmark_comparison.main_overload?.category === g.category;
                    const flagColor = isLever ? "#0B7A3B"
                                                : isOverload ? "#B91C1C"
                                                : "#94A3B8";
                    return (
                      <div key={g.category} className="text-[13px]"
                            data-testid={`abi-benchmark-gap-${g.category}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="w-32 capitalize">{g.label_fr}</span>
                          {isLever && (
                            <span className="text-[10px] px-2 py-0.5 rounded uppercase font-bold"
                                    style={{ backgroundColor: "#DCFCE7", color: "#0B7A3B" }}>
                              principal levier
                            </span>
                          )}
                          {isOverload && (
                            <span className="text-[10px] px-2 py-0.5 rounded uppercase font-bold"
                                    style={{ backgroundColor: "#FEE2E2", color: "#B91C1C" }}>
                              sur-investi
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {/* User bar */}
                          <div className="w-16 text-right font-mono text-[11px] text-slate-700">
                            {g.user_pct.toFixed(0)}%
                          </div>
                          <div className="flex-1 bg-gray-100 h-2 rounded overflow-hidden relative">
                            <div className="h-full absolute top-0 left-0"
                                  style={{ width: `${Math.min(100, g.user_pct * 2)}%`,
                                            backgroundColor: "#0B2545" }} />
                            {/* Benchmark marker */}
                            <div className="absolute top-0 h-full border-l-2"
                                  style={{ left: `${Math.min(100, g.benchmark_pct * 2)}%`,
                                            borderColor: flagColor }} />
                          </div>
                          <div className="w-16 text-left font-mono text-[11px]"
                                style={{ color: flagColor }}>
                            b. {g.benchmark_pct.toFixed(0)}%
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Top habits from best performers */}
              {(report.benchmark_comparison.habit_recommendations_fr || []).length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest font-bold mb-2"
                        style={{ color: "#7A5B14" }}>
                    Habitudes des meilleurs à adopter
                  </div>
                  <ul className="space-y-1 text-[13px]" data-testid="abi-benchmark-habits">
                    {report.benchmark_comparison.habit_recommendations_fr.map((h, i) => (
                      <li key={i} className="flex gap-2 items-start">
                        <span style={{ color: "#C5A880" }}>★</span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
